import { FC, FormEvent, useCallback, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, Flex, HStack, Input, Text, VStack } from '@bonusly/design-system';

import { useGranolaConnect, useGranolaDisconnect, useGranolaStatus } from '../../queries/granola';
import { GranolaLogo } from './granola-logo';

export const GranolaConnectCard: FC = () => {
	const { data: status, isLoading: isLoadingStatus } = useGranolaStatus();
	const { mutateAsync: connect, isLoading: isConnecting } = useGranolaConnect();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useGranolaDisconnect();
	const { track } = useAnalytics();

	const [apiKey, setApiKey] = useState('');
	const [error, setError] = useState('');

	const handleConnect = useCallback(
		async (e: FormEvent) => {
			e.preventDefault();
			setError('');
			track(AnalyticsEvent.Granola.ConnectClicked);
			try {
				await connect(apiKey);
				track(AnalyticsEvent.Granola.Connected);
				setApiKey('');
			} catch {
				setError('Failed to connect. Please check your API key and try again.');
			}
		},
		[connect, apiKey, track]
	);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Granola.Disconnected);
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, track]);

	if (isLoadingStatus) return null;

	const isConnected = status?.status === 'connected';

	return (
		<Box py={3} borderBottom="1px solid" borderColor="border.soft">
			<Flex alignItems="center" justifyContent="space-between">
				<HStack spacing={3} align="center">
					<Box color="text.default" flexShrink={0} display="inline-flex" alignItems="center">
						<GranolaLogo width="24" height="24" />
					</Box>
					<VStack spacing={0} align="flex-start">
						<Text size="md" variant="strong" color="text.default">
							Granola
						</Text>
						<Text size="sm" color="text.softer">
							{isConnected ? 'Connected' : 'Not connected'}
						</Text>
					</VStack>
				</HStack>
				{isConnected ? (
					<Button size="md" variant="ghost" colorScheme="secondary" onClick={handleDisconnect} isLoading={isDisconnecting}>
						Disconnect
					</Button>
				) : (
					<Flex as="form" onSubmit={handleConnect} alignItems="center" gap={2} flex={1} ml={4}>
						<Input type="password" size="md" flex={1} placeholder="Granola API key (grn_...)" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
						<Button size="md" colorScheme="primary" type="submit" isLoading={isConnecting} isDisabled={!apiKey.trim()} flexShrink={0}>
							Connect
						</Button>
					</Flex>
				)}
			</Flex>
			{error && (
				<Text size="md" color="text.danger" mt={2}>
					{error}
				</Text>
			)}
		</Box>
	);
};
